import { searchService } from '@/modules/hub/features/Search/services/searchService';
import type { GetResourceSearchResultsRequest, GetResourceSearchResultsItem } from '@/api/getResourceSearchResults/types';

/**
 * Service layer for News to use Algolia
 * Transforms between News format and Algolia format
 */
class NewsAlgoliaService {
  /**
   * Search news using Algolia
   * Returns data in the format News UI expects
   */
  async searchNews(params: GetResourceSearchResultsRequest): Promise<{
    resources: GetResourceSearchResultsItem[];
    totalCount: number;
  }> {
    // Initialize with 'news' context to only show News type
    await searchService.initialize(false, 'news');

    // Calculate page for Algolia (0-indexed)
    const algoliaPage = (params.page || 1) - 1;
    const hitsPerPage = params.pageSize || 10;

    // Map sortBy and sortOrder to sort option
    let sortOption = 'newest'; // default
    if (params.sortBy && params.sortOrder) {
      if (params.sortBy === 'publishStartDate' && params.sortOrder === 'desc') {
        sortOption = 'newest';
      } else if (params.sortBy === 'publishStartDate' && params.sortOrder === 'asc') {
        sortOption = 'oldest';
      } else if (params.sortBy === 'viewCount' && params.sortOrder === 'desc') {
        sortOption = 'mostViewed';
      } else if (params.sortBy === 'title' && params.sortOrder === 'asc') {
        sortOption = 'alphabetical';
      }
    }

    // Search Algolia - News page has no filters, just search
    const result = await searchService.searchResources(
      params.searchTerm || '',  // Search term
      { type: 'resources' }, // Only search resources
      algoliaPage,
      hitsPerPage,
      sortOption,
      false  // News doesn't use favorites
    );

    // Transform Algolia hits to News format
    const resources: GetResourceSearchResultsItem[] = result.hits.map((hit: any) => ({
      id: hit.resourceId || hit.objectID?.replace('resource_', '') || Math.random() * 100000,
      uuid: hit.uuid || '',
      slug: hit.slug || '',
      title: hit.title || '',
      searchDescription: hit.searchDescription || null,
      isPublished: hit.isPublished || false,
      thumbnailUrl: hit.thumbnail || null,
      thumbnailFileUuid: null,
      primaryIcon: hit.primaryIcon || null,
      isFeatured: hit.isFeatured || false,
      resourceType: hit.resourceType || null,
      createdOrPublishStartDate: hit.publishStartDate || hit.createdAt || null,
      viewCount: hit.viewCount || 0,
      backgroundColor: hit.backgroundColor || null,
      textColor: hit.textColor || null,
      borderColor: hit.borderColor || null,
      contentUrl: hit.url || null,
      isFavorited: false,
    }));

    return {
      resources,
      totalCount: result.total,
    };
  }
}

export const newsAlgoliaService = new NewsAlgoliaService();
